import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Loader2, Download, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DownloadInvoicesDialog — lets the user download every invoice for one
 * customer as a ZIP of individual PDFs. Optional date range narrows it down.
 */
export default function DownloadInvoicesDialog({ open, onOpenChange, customer }) {
  const { language } = useLanguage();
  const T = (ar, en) => (language === 'ar' ? ar : en);

  const [useRange, setUseRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const reset = () => {
    setUseRange(false);
    setStartDate('');
    setEndDate('');
    setError('');
    setSuccess('');
    setLoading(false);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleDownload = async () => {
    if (!customer?.id) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const params = {};
      if (useRange) {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const res = await axios.get(`${API}/customers/${customer.id}/invoices-zip`, {
        params,
        responseType: 'blob',
      });
      const count = res.headers['x-invoice-count'] || '?';

      // Extract filename from Content-Disposition or fallback
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `invoices_${customer.id}.zip`;

      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setSuccess(T(`تم تنزيل ${count} فاتورة بنجاح.`, `Downloaded ${count} invoice(s) successfully.`));
    } catch (e) {
      // axios error with blob → need to read body as text
      let detail = '';
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try {
            detail = JSON.parse(txt)?.detail;
          } catch {
            detail = txt;
          }
        } else {
          detail = e?.response?.data?.detail || '';
        }
      } catch { /* noop */ }
      if (e?.response?.status === 404) {
        setError(T('لا توجد فواتير في هذا النطاق.', detail || 'No invoices found in this range.'));
      } else {
        setError(detail || T('فشل التحميل.', 'Download failed.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="download-invoices-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {T('تنزيل كل الفواتير', 'Download All Invoices')}
            {customer && (
              <span className="ms-2 text-base text-muted-foreground">— {customer.name}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {T(
              'سيتم تنزيل جميع فواتير العميل كملف ZIP يحتوي على PDF لكل فاتورة.',
              'All invoices for this customer will be downloaded as a ZIP containing one PDF per invoice.',
            )}
          </p>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={useRange}
              onChange={(e) => setUseRange(e.target.checked)}
              data-testid="download-use-range"
            />
            {T('تحديد نطاق تاريخ (اختياري)', 'Limit to a date range (optional)')}
          </label>

          {useRange && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dl-start">{T('من تاريخ', 'From')}</Label>
                <Input
                  id="dl-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="download-start-date"
                />
              </div>
              <div>
                <Label htmlFor="dl-end">{T('إلى تاريخ', 'To')}</Label>
                <Input
                  id="dl-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="download-end-date"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="download-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          {success && (
            <div className="text-green-700 text-sm bg-green-50 border border-green-200 rounded p-2" data-testid="download-success">
              {success}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="download-cancel">
            {T('إغلاق', 'Close')}
          </Button>
          <Button onClick={handleDownload} disabled={loading} data-testid="download-confirm">
            {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Download className="me-2 h-4 w-4" />}
            {T('تنزيل ZIP', 'Download ZIP')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
